import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import PageTitle from "../../components/PageTitle/PageTitle";
import AppVersionService from "./Locality/Service/appVersionService";

const SUGGESTED_UPDATE_TITLE = "Update Required";
const SUGGESTED_UPDATE_MESSAGE =
  "A new version of the app is available. Please update to the latest version to continue with the best experience and security fixes.";

const emptyForm = {
  androidTeacherVersion: "",
  androidTeacherLink: "",
  androidParentVersion: "",
  androidParentLink: "",
  iosTeacherVersion: "",
  iosTeacherLink: "",
  iosParentVersion: "",
  iosParentLink: "",
  web: "",
  updateTitle: SUGGESTED_UPDATE_TITLE,
  updateMessage: SUGGESTED_UPDATE_MESSAGE,
  forceUpdate: false,
};

const readRole = (role) => {
  if (!role) return { version: "", link: "" };
  if (typeof role === "string") return { version: role, link: "" };
  return {
    version: role.version != null ? String(role.version) : "",
    link: role.link != null ? String(role.link) : "",
  };
};

const mapDataToForm = (data) => {
  const androidTeacher = readRole(data?.android?.teacher);
  const androidParent = readRole(data?.android?.parent);
  const iosTeacher = readRole(data?.ios?.teacher || data?.iso?.teacher);
  const iosParent = readRole(data?.ios?.parent || data?.iso?.parent);

  return {
    androidTeacherVersion: androidTeacher.version,
    androidTeacherLink: androidTeacher.link,
    androidParentVersion: androidParent.version,
    androidParentLink: androidParent.link,
    iosTeacherVersion: iosTeacher.version,
    iosTeacherLink: iosTeacher.link,
    iosParentVersion: iosParent.version,
    iosParentLink: iosParent.link,
    web: data?.web != null ? String(data.web) : "",
    updateTitle: data?.updateTitle || data?.title || "",
    updateMessage: data?.updateMessage || data?.message || "",
    forceUpdate: Boolean(data?.forceUpdate),
  };
};

const mapFormToPayload = (values) => ({
  android: {
    teacher: {
      version: String(values.androidTeacherVersion || "").trim(),
      link: String(values.androidTeacherLink || "").trim(),
    },
    parent: {
      version: String(values.androidParentVersion || "").trim(),
      link: String(values.androidParentLink || "").trim(),
    },
  },
  ios: {
    teacher: {
      version: String(values.iosTeacherVersion || "").trim(),
      link: String(values.iosTeacherLink || "").trim(),
    },
    parent: {
      version: String(values.iosParentVersion || "").trim(),
      link: String(values.iosParentLink || "").trim(),
    },
  },
  web: String(values.web || "").trim(),
  updateTitle: String(values.updateTitle || "").trim(),
  updateMessage: String(values.updateMessage || "").trim(),
  forceUpdate: Boolean(values.forceUpdate),
});

const renderPlatformFields = (platformLabel, prefix, form, onFieldChange) => (
  <>
    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 16, color: "#0f2d4d" }}>
      {platformLabel}
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Teacher app version"
          value={form[`${prefix}TeacherVersion`] ?? ""}
          onChange={onFieldChange(`${prefix}TeacherVersion`)}
          placeholder="e.g. 3.2.1"
          inputProps={{ inputMode: "decimal" }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Teacher store link"
          value={form[`${prefix}TeacherLink`] ?? ""}
          onChange={onFieldChange(`${prefix}TeacherLink`)}
          placeholder="https://play.google.com/..."
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Parent app version"
          value={form[`${prefix}ParentVersion`] ?? ""}
          onChange={onFieldChange(`${prefix}ParentVersion`)}
          placeholder="e.g. 3.2.1"
          inputProps={{ inputMode: "decimal" }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Parent store link"
          value={form[`${prefix}ParentLink`] ?? ""}
          onChange={onFieldChange(`${prefix}ParentLink`)}
          placeholder="https://play.google.com/..."
        />
      </Grid>
    </Grid>
  </>
);

export default function AppVersion() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin =
    String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

  const [form, setForm] = useState(emptyForm);
  const [configId, setConfigId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const isEditMode = Boolean(configId);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AppVersionService.get();
      const data = res?.data || res;
      if (data && (data._id || data.android || data.web !== undefined)) {
        setConfigId(data._id || "exists");
        setForm(mapDataToForm(data));
        setUpdatedAt(data.updatedAt ? new Date(data.updatedAt).toLocaleString() : "");
      } else {
        setConfigId(null);
        setForm(emptyForm);
        setUpdatedAt("");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setConfigId(null);
        setForm(emptyForm);
        setUpdatedAt("");
      } else {
        setToast(err?.response?.data?.message || err?.message || "Failed to load app version config");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      loadConfig();
    }
  }, [isSuperAdmin, loadConfig]);

  const handleFieldChange = useCallback((field) => (event) => {
    const value = event && event.target != null ? event.target.value : "";
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleForceUpdateChange = useCallback((event) => {
    const checked = event?.target != null ? Boolean(event.target.checked) : false;
    setForm((prev) => ({ ...prev, forceUpdate: checked }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const payload = mapFormToPayload(form);
    try {
      const res = isEditMode
        ? await AppVersionService.update(payload)
        : await AppVersionService.create(payload);
      const data = res?.data || res;
      setConfigId(data?._id || "exists");
      setForm(mapDataToForm(data));
      setUpdatedAt(data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : "");
      setToast(res?.message || (isEditMode ? "App version updated" : "App version created"));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 && !isEditMode) {
        try {
          const res = await AppVersionService.update(payload);
          const data = res?.data || res;
          setConfigId(data?._id || "exists");
          setForm(mapDataToForm(data));
          setUpdatedAt(data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : "");
          setToast(res?.message || "App version updated");
        } catch (updateErr) {
          setToast(updateErr?.response?.data?.message || updateErr?.message || "Failed to save");
        }
      } else {
        setToast(err?.response?.data?.message || err?.message || "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageTitle title="App Version Config" />

      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, maxWidth: 720 }}>
        Configure minimum app versions, store links, and the update prompt shown to users. Mobile apps read these values from{" "}
        <code>GET /api/app-version</code>.
      </Typography>

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {renderPlatformFields("Android", "android", form, handleFieldChange)}

              <Box mt={3}>
                {renderPlatformFields("iOS", "ios", form, handleFieldChange)}
              </Box>

              <Box mt={3} mb={1}>
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#0f2d4d" }}>
                  Web
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Web version"
                    value={form.web ?? ""}
                    onChange={handleFieldChange("web")}
                    placeholder="e.g. 3.2.1"
                    inputProps={{ inputMode: "decimal" }}
                  />
                </Grid>
              </Grid>

              <Box mt={3} mb={1}>
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#0f2d4d" }}>
                  Update Prompt
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  Title and message shown to users when their app version is below the configured minimum.
                  Suggested: <strong>{SUGGESTED_UPDATE_TITLE}</strong> — {SUGGESTED_UPDATE_MESSAGE}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Update title"
                    value={form.updateTitle ?? ""}
                    onChange={handleFieldChange("updateTitle")}
                    placeholder={SUGGESTED_UPDATE_TITLE}
                    helperText={`Suggested: "${SUGGESTED_UPDATE_TITLE}"`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Update message"
                    value={form.updateMessage ?? ""}
                    onChange={handleFieldChange("updateMessage")}
                    placeholder={SUGGESTED_UPDATE_MESSAGE}
                    helperText="Suggested message is pre-filled for new configs. Edit as needed for your school."
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={Boolean(form.forceUpdate)}
                        onChange={handleForceUpdateChange}
                      />
                    }
                    label={
                      <Box>
                        <Typography style={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f2d4d" }}>
                          Force update
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          When enabled, users must update the app and cannot dismiss the update prompt.
                          When disabled, the update prompt is optional.
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>

              {updatedAt && (
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 16 }}>
                  Last updated: {updatedAt}
                </Typography>
              )}

              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  style={{ textTransform: "none", fontWeight: 600, backgroundColor: "rgb(15 59 98)" }}
                >
                  {saving ? "Saving..." : isEditMode ? "Update" : "Create"}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
